import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, fmtMoney } from "@/lib/api";
import { ArrowLeft, Printer } from "lucide-react";
import { toast } from "sonner";
import { LOGO_URL } from "@/components/Brand";

const monthName = (m) => ["January","February","March","April","May","June","July","August","September","October","November","December"][m-1];

function Line({ label, value, isDeduction, isBold, currency }) {
  return (
    <tr className={isBold ? "border-t border-neutral-950" : ""}>
      <td className={`py-1.5 ${isBold ? "font-semibold pt-2" : "text-neutral-600"} font-mono text-sm`}>{label}</td>
      <td className={`py-1.5 text-right font-mono text-sm ${isBold ? "font-semibold pt-2" : ""} ${isDeduction ? "text-[#FF3B30]" : ""}`}>
        {isDeduction ? "− " : ""}{fmtMoney(value, currency)}
      </td>
    </tr>
  );
}

export default function PayslipView() {
  const { id } = useParams();
  const [slip, setSlip] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get(`/payslips/${id}`); setSlip(data); }
      catch (e) { toast.error("Not found"); }
      finally { setLoading(false); }
    })();
  }, [id]);

  if (loading) return <div className="p-10 font-mono text-xs text-neutral-500 uppercase tracking-widest">Loading…</div>;
  if (!slip) return <div className="p-10 font-mono text-xs text-neutral-500">Payslip not found.</div>;

  const currency = slip.company?.currency || "USD";
  const b = slip.breakdown || {};
  const isIndia = b.regime === "india";

  return (
    <div>
      <div className="border-b border-neutral-200 bg-white px-8 py-6 flex items-center justify-between print:hidden">
        <Link to="/app/payslips" data-testid="btn-back" className="inline-flex items-center gap-2 font-mono text-sm text-neutral-600 hover:text-neutral-950 transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back to payslips
        </Link>
        <button onClick={() => window.print()} data-testid="btn-print" className="inline-flex items-center gap-2 bg-[#002FA7] text-white px-4 py-2.5 font-mono text-sm hover:bg-neutral-950 transition-colors">
          <Printer className="w-4 h-4" /> Print / Save PDF
        </button>
      </div>

      <div className="p-8">
        <div className="max-w-3xl mx-auto bg-white border border-neutral-950 print:border-0" data-testid="payslip-doc">
          {/* Header */}
          <div className="border-b-2 border-neutral-950 px-8 py-6 flex items-start justify-between">
            <div className="flex items-start gap-4">
              <img src={LOGO_URL} alt="HG Consultancy" className="h-16 w-auto" />
              <div>
              <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#8C6F1E] mb-2">Issued via HarguonPayroll.com</div>
                <div className="font-serif font-black text-3xl tracking-tight">{slip.company?.name}</div>
                <div className="text-xs text-neutral-500 font-mono mt-1">{slip.company?.address || ""}</div>
                {slip.company?.gstin && (
                  <div className="mt-2 text-[11px] font-mono text-neutral-600">
                    <span className="text-neutral-400">GSTIN</span> <span className="text-neutral-950 tracking-wider">{slip.company.gstin}</span>
                    {slip.company.pan && <>  ·  <span className="text-neutral-400">PAN</span> <span className="text-neutral-950">{slip.company.pan}</span></>}
                    {slip.company.tan && <>  ·  <span className="text-neutral-400">TAN</span> <span className="text-neutral-950">{slip.company.tan}</span></>}
                  </div>
                )}
                {isIndia && <div className="mt-2 inline-block text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 border border-[#C9A961] text-[#8C6F1E]">India · statutory</div>}
              </div>
            </div>
            <div className="text-right">
              <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">Payslip №</div>
              <div className="font-mono text-sm">{slip.id.slice(0, 8).toUpperCase()}</div>
              <div className="mt-2 font-serif font-black text-2xl tracking-tight">{monthName(slip.month)} {slip.year}</div>
            </div>
          </div>

          {/* Employee */}
          <div className="grid grid-cols-2 border-b border-neutral-200">
            <div className="p-6 border-r border-neutral-200">
              <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">Employee</div>
              <div className="mt-1 font-semibold text-lg">{slip.employee_name}</div>
              <div className="font-mono text-xs text-neutral-500 mt-1">{slip.employee_code}</div>
            </div>
            <div className="p-6 grid grid-cols-2 gap-4">
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">Department</div>
                <div className="mt-1 font-mono text-sm">{slip.department || "—"}</div>
              </div>
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">Designation</div>
                <div className="mt-1 font-mono text-sm">{slip.designation || "—"}</div>
              </div>
            </div>
          </div>

          {/* Breakdown */}
          <div className="grid grid-cols-2 border-b border-neutral-200">
            <div className="p-6 border-r border-neutral-200">
              <div className="text-[10px] font-mono uppercase tracking-widest text-[#002FA7] mb-4">Earnings</div>
              <table className="w-full">
                <tbody>
                  <Line label="Basic" value={b.basic} currency={currency} />
                  {isIndia && <Line label="Dearness Allowance" value={b.da} currency={currency} />}
                  <Line label="HRA" value={b.hra} currency={currency} />
                  <Line label="Allowances" value={b.allowances} currency={currency} />
                  <Line label="Gross" value={b.gross} isBold currency={currency} />
                </tbody>
              </table>
            </div>
            <div className="p-6">
              <div className="text-[10px] font-mono uppercase tracking-widest text-[#FF3B30] mb-4">Deductions</div>
              <table className="w-full">
                <tbody>
                  {isIndia ? (
                    <>
                      <Line label="EPF (12% of Basic+DA)" value={b.pf} isDeduction currency={currency} />
                      <Line label="ESI (0.75%)" value={b.esi} isDeduction currency={currency} />
                      <Line label={`Professional Tax${b.state ? ` · ${b.state}` : ""}`} value={b.pt} isDeduction currency={currency} />
                      <Line label="Labour Welfare Fund" value={b.lwf} isDeduction currency={currency} />
                      <Line label="TDS (monthly)" value={b.tds} isDeduction currency={currency} />
                      <Line label="Other" value={b.other_deductions} isDeduction currency={currency} />
                    </>
                  ) : (
                    <>
                      <Line label="Fixed deductions" value={b.deductions} isDeduction currency={currency} />
                      <Line label={`Tax (${b.tax_percent || 0}%)`} value={b.tax} isDeduction currency={currency} />
                    </>
                  )}
                  <Line label="Total" value={b.total_deductions} isBold isDeduction currency={currency} />
                </tbody>
              </table>
            </div>
          </div>

          {/* Net */}
          <div className="bg-neutral-950 text-white px-8 py-6 flex items-center justify-between">
            <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#FF3B30]">Net pay</div>
            <div className="font-display font-black text-5xl tracking-tighter">{fmtMoney(b.net, currency)}</div>
          </div>

          <div className="px-8 py-4 flex items-center justify-between text-xs text-neutral-500 font-mono">
            <span>Generated {slip.generated_at?.slice(0, 10)}</span>
            <span>{isIndia ? "TDS · New Regime FY 2025-26 · 87A applied" : "Issued via HARGUON PAYROLL"}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
